import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { LoadingSpinner } from '@/components/Generic'
import type { Base64File } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { devLog } from '@methods/devLog'
import {
  deleteDocument,
  getContext,
  isTerminalTransactionError,
  navigateToComplete,
  submitAnswers,
  uploadDocumentFile,
} from '@methods/dataCollection'
import { computeDefaultFrom } from './helpers'
import type {
  Answers,
  DocumentRequest,
  RetryReason,
  Screen,
  Step,
  UploadedDocument,
} from '@/types/dataCollection'
import { KNOWN_RETRY_REASONS } from '@/types/dataCollection'
import { QuestionsScreen } from './QuestionsScreen'
import { FileUploadScreen } from './FileUploadScreen'
import { InfoScreen } from './InfoScreen'
import { ReviewScreen } from './ReviewScreen'
import { SuccessScreen } from './SuccessScreen'
import { RetryNotice } from './RetryNotice'
import './DataCollection.css'

export const DataCollectionPage = observer(() => {
  const store = useContext(StoreContext)

  const [loading, setLoading] = useState(true)
  const [fatalError, setFatalError] = useState(false)
  const [step, setStep] = useState<Step | null>(null)
  const [answers, setAnswers] = useState<Answers>({})
  // Saved `defaultFrom` targets that hold a computed value (not typed by the user). Reported by
  // each questions screen on submit; lets a later visit refresh them when their sources changed
  // on another screen. Session-local: after a reload every saved value is kept as-is.
  const [derivedAnswers, setDerivedAnswers] = useState<ReadonlySet<string>>(() => new Set())
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [history, setHistory] = useState<Screen[]>([])
  // Set while the user edits a screen they reached through a review screen's edit button: the
  // review to return to (sent as `returnToScreenId`) and the back-stack length to restore so
  // „Zurück" from the review does not lead to the edited screen.
  const [editReturn, setEditReturn] = useState<{ screenId: string; historyLength: number } | null>(
    null
  )
  // Set when the backend rewound the user here (e.g. open banking failed). Shown on the first
  // screen only: cleared once the user moves on.
  const [retryReason, setRetryReason] = useState<RetryReason | null>(null)

  useEffect(() => {
    getContext()
      .then((ctx) => {
        setStep(ctx.step)
        setAnswers(ctx.answers ?? {})
        setUploadedDocuments(ctx.uploadedDocuments ?? [])
        setHistory(ctx.previousScreens ?? [])
        setRetryReason(
          ctx.retryReason && KNOWN_RETRY_REASONS.includes(ctx.retryReason) ? ctx.retryReason : null
        )
        setLoading(false)
      })
      .catch((error) => {
        devLog('DataCollection get-context failed:', error)
        setFatalError(true)
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    if (step?.kind === 'complete' && !step.content) {
      navigateToComplete()
    }
  }, [step])

  const submitScreen = async (
    screenId: string,
    screenAnswers: Answers,
    derivedIds: string[] = []
  ) => {
    setSubmitting(true)
    try {
      const res = await submitAnswers(screenId, screenAnswers, editReturn?.screenId)
      const screenQuestionIds =
        step?.kind === 'screen' && step.screen.type === 'questions'
          ? step.screen.questions.map((question) => question.id)
          : []
      setAnswers((prev) => {
        const next = { ...prev }
        for (const id of screenQuestionIds) delete next[id]
        return { ...next, ...screenAnswers }
      })
      setDerivedAnswers((prev) => {
        const next = new Set(prev)
        for (const id of screenQuestionIds) next.delete(id)
        for (const id of derivedIds) next.add(id)
        return next
      })
      if (editReturn) {
        setEditReturn(null)
        const returnedToReview =
          res.step.kind === 'screen' && res.step.screen.id === editReturn.screenId
        // Refresh screen definitions and answers (derived totals, dynamic select options) from the
        // server after every review edit. On failure, fall back to local state for returns to the
        // same review screen.
        try {
          const ctx = await getContext()
          setHistory(ctx.previousScreens ?? [])
          setAnswers(ctx.answers ?? {})
        } catch (ctxError) {
          devLog('DataCollection get-context failed after edit:', ctxError)
          if (returnedToReview) {
            const newHistory = history.slice(0, editReturn.historyLength)
            setHistory(newHistory)
            const refreshedDerived = new Set(derivedAnswers)
            for (const id of screenQuestionIds) refreshedDerived.delete(id)
            for (const id of derivedIds) refreshedDerived.add(id)
            setAnswers((merged) => {
              const refreshed = { ...merged }
              for (const histScreen of newHistory) {
                if (histScreen.type !== 'questions') continue
                for (const question of histScreen.questions) {
                  if (question.type !== 'number') continue
                  if (!question.defaultFrom || question.applyDefaultLabel) continue
                  if (!refreshedDerived.has(question.id)) continue
                  const computed = computeDefaultFrom(question, refreshed)
                  if (computed !== undefined) refreshed[question.id] = computed
                }
              }
              return refreshed
            })
          }
          // Without refreshed context, the old back stack may contain inapplicable screens.
          if (!returnedToReview) setHistory([])
        }
      } else if (step?.kind === 'screen') {
        setHistory((prev) => [...prev, step.screen])
      }
      setRetryReason(null)
      setStep(res.step)
    } catch (error) {
      if (isTerminalTransactionError(error)) {
        navigateToComplete()
        return
      }
      devLog('DataCollection submit-answers failed:', error)
      setFatalError(true)
    } finally {
      setSubmitting(false)
    }
  }

  // Upload/delete errors are surfaced per slot by UploadSlot. A frozen or completed transaction
  // ends the flow for the user: navigate on, and still reject so the slot stops its queue
  // instead of treating the file as uploaded (navigation does not interrupt the current task).
  const handleUpload = async (request: DocumentRequest, file: Base64File) => {
    try {
      const res = await uploadDocumentFile(request, file)
      setUploadedDocuments(res.uploadedDocuments)
      setStep(res.step)
    } catch (error) {
      if (isTerminalTransactionError(error)) navigateToComplete()
      throw error
    }
  }

  const handleDelete = async (documentId: string) => {
    try {
      const res = await deleteDocument(documentId)
      setUploadedDocuments(res.uploadedDocuments)
      setStep(res.step)
    } catch (error) {
      if (isTerminalTransactionError(error)) navigateToComplete()
      throw error
    }
  }

  const handleBack = () => {
    const previous = history[history.length - 1]
    if (!previous) return
    setRetryReason(null)
    setEditReturn(null)
    setStep({ kind: 'screen', screen: previous })
    setHistory((prev) => prev.slice(0, -1))
  }

  // A review screen's edit button: show the referenced screen (it is behind the user, so its
  // definition is in the stack), prefilled from the saved answers; the review goes onto the stack
  // so „Zurück" returns to it, and the submit returns to it through `returnToScreenId`.
  const handleEdit = (screenId: string) => {
    if (step?.kind !== 'screen') return
    const target = history.find((screen) => screen.id === screenId)
    if (!target) return
    const review = step.screen
    setEditReturn({ screenId: review.id, historyLength: history.length })
    setHistory((prev) => [...prev, review])
    setStep({ kind: 'screen', screen: target })
  }

  if (loading) {
    return (
      <div className="page-container">
        <LoadingSpinner />
      </div>
    )
  }

  if (fatalError) {
    const { dataCollectionPage: trans } = store.TranslationsState.translations
    return (
      <div className="page-container">
        <p className="generic__paragraph">{trans?.errors.generic}</p>
      </div>
    )
  }

  if (!step) {
    return (
      <div className="page-container">
        <LoadingSpinner />
      </div>
    )
  }

  if (step.kind === 'complete') {
    return (
      <div className="data-collection">
        {step.content ? <SuccessScreen content={step.content} /> : <LoadingSpinner />}
      </div>
    )
  }

  const { screen } = step
  const canGoBack = history.length > 0
  const notice = retryReason ? <RetryNotice reason={retryReason} /> : undefined

  const renderScreen = () => {
    switch (screen.type) {
      case 'questions':
        return (
          <QuestionsScreen
            key={screen.id}
            screen={screen}
            notice={notice}
            savedAnswers={answers}
            derivedAnswers={derivedAnswers}
            submitting={submitting}
            canGoBack={canGoBack}
            onBack={handleBack}
            onSubmit={(screenAnswers, derivedIds) =>
              submitScreen(screen.id, screenAnswers, derivedIds)
            }
          />
        )
      case 'fileUpload':
        return (
          <FileUploadScreen
            key={screen.id}
            screen={screen}
            notice={notice}
            uploadedDocuments={uploadedDocuments}
            submitting={submitting}
            canGoBack={canGoBack}
            onBack={handleBack}
            onUpload={handleUpload}
            onDelete={handleDelete}
            onSubmit={() => submitScreen(screen.id, {})}
          />
        )
      case 'info':
        return (
          <InfoScreen
            key={screen.id}
            screen={screen}
            notice={notice}
            submitting={submitting}
            canGoBack={canGoBack}
            onBack={handleBack}
            onSubmit={() => submitScreen(screen.id, {})}
          />
        )
      case 'review':
        return (
          <ReviewScreen
            key={screen.id}
            screen={screen}
            screensById={new Map(history.map((previous) => [previous.id, previous]))}
            savedAnswers={answers}
            notice={notice}
            submitting={submitting}
            onEdit={handleEdit}
            onSubmit={() => submitScreen(screen.id, {})}
          />
        )
    }
  }

  return <div className="data-collection">{renderScreen()}</div>
})
